import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import ClientRow, { clampMarginInputValue } from "./ClientRow";
import GlobalIcon from "../../components/GlobalIcon/GlobalIcon";
import { DEBOUNCE_MS } from "./constants";
import styles from "./OrderInitiatePage.module.css";

const ClientIcon = () => (
  <span className={styles.inlineIconMuted} aria-hidden>
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  </span>
);

const HeaderHelpIcon = () => (
  <span className={styles.selectionHeaderHelp} title="Help" role="img" aria-label="Help">
    ?
  </span>
);

const BASKET_SEARCH_HISTORY_KEY = "orderInitiate_basketSearchHistory";
const MAX_SEARCH_HISTORY = 8;

function loadSearchHistory() {
  try {
    const raw = window.localStorage.getItem(BASKET_SEARCH_HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, MAX_SEARCH_HISTORY) : [];
  } catch {
    return [];
  }
}

function saveSearchHistory(history) {
  try {
    window.localStorage.setItem(BASKET_SEARCH_HISTORY_KEY, JSON.stringify(history));
  } catch {
    /* ignore */
  }
}

/** Same display rule as ClientRow multiplier (manual entry or suggested). */
function displayMultiplierForRow(row) {
  const manual = row.multiplier !== undefined && String(row.multiplier).trim() !== "";
  return manual ? String(row.multiplier) : String(row.suggestedMultiplier ?? "");
}

/** Numeric sort for formatted Available Margin cells (locale strings, commas, em dash) */
function parseAvailableMarginForSort(display) {
  if (display == null || display === "" || display === "—") return 0;
  const n = Number(String(display).replace(/,/g, "").trim());
  return Number.isNaN(n) ? 0 : n;
}

function CombinedSelectionSection({
  baskets,
  selectedBasketIndices,
  toggleBasket,
  hasBasketSelected,
  basketClientsLoading,
  clientRows,
  updateClientRow,
  applyMarginToDeployToAllClients,
  applyMultiplierToAllClients,
  allClientsChecked,
  someClientsChecked,
  onClientHeaderCheckboxChange,
  clientSelectionPhase = "browse",
  onClientSelectionNext,
  onClientSelectionBack,
}) {
  const headerCheckboxRef = useRef(null);
  const basketSelectWrapRef = useRef(null);
  const basketDropdownSearchRef = useRef(null);
  const indeterminate = clientRows.length > 0 && someClientsChecked && !allClientsChecked;
  const [clientSearch, setClientSearch] = useState("");
  const [basketSearch, setBasketSearch] = useState("");
  const [searchHistory, setSearchHistory] = useState(() => loadSearchHistory());
  const [isBasketDropdownOpen, setIsBasketDropdownOpen] = useState(false);
  const [clientSort, setClientSort] = useState(() => ({ key: null, dir: "asc" }));
  const isBrowsePhase = clientSelectionPhase === "browse";

  useEffect(() => {
    const id = window.setTimeout(() => saveSearchHistory(searchHistory), 400);
    return () => window.clearTimeout(id);
  }, [searchHistory]);

  useEffect(() => {
    if (!isBrowsePhase) return;
    if (headerCheckboxRef.current) headerCheckboxRef.current.indeterminate = indeterminate;
  }, [indeterminate, isBrowsePhase]);

  useEffect(() => {
    if (!isBasketDropdownOpen) return;
    const handleClickOutside = (event) => {
      if (basketSelectWrapRef.current && !basketSelectWrapRef.current.contains(event.target)) {
        setIsBasketDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isBasketDropdownOpen]);

  useEffect(() => {
    if (!isBasketDropdownOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setIsBasketDropdownOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isBasketDropdownOpen]);

  useEffect(() => {
    if (!isBasketDropdownOpen || !basketDropdownSearchRef.current) return;
    const id = window.setTimeout(() => basketDropdownSearchRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [isBasketDropdownOpen]);

  useEffect(() => {
    setClientSearch("");
    setIsBasketDropdownOpen(false);
  }, [clientSelectionPhase]);

  const displayBaskets = useMemo(() => (Array.isArray(baskets) ? baskets : []), [baskets]);

  const rowsForTable = useMemo(() => {
    if (clientSelectionPhase === "selectedReview") return clientRows.filter((r) => r.checked);
    return clientRows;
  }, [clientSelectionPhase, clientRows]);

  const filteredClients = useMemo(() => {
    if (!clientSearch.trim()) return rowsForTable;
    const search = clientSearch.toLowerCase();
    return rowsForTable.filter((row) => row.clientCode?.toLowerCase().includes(search));
  }, [rowsForTable, clientSearch]);

  const sortedClients = useMemo(() => {
    const list = [...filteredClients];
    if (!clientSort.key) return list;
    const mult = clientSort.dir === "asc" ? 1 : -1;
    if (clientSort.key === "clientCode") {
      list.sort((a, b) =>
        mult *
        String(a.clientCode ?? "").localeCompare(String(b.clientCode ?? ""), undefined, {
          sensitivity: "base",
          numeric: true,
        })
      );
      return list;
    }
    if (clientSort.key === "availableMargin") {
      list.sort(
        (a, b) =>
          mult * (parseAvailableMarginForSort(a.availableMargin) - parseAvailableMarginForSort(b.availableMargin))
      );
      return list;
    }
    return list;
  }, [filteredClients, clientSort]);

  const sortedClientIdsRef = useRef([]);
  sortedClientIdsRef.current = sortedClients.map((r) => r.id);

  const unifiedMarginToDeploy = useMemo(() => {
    if (!sortedClients.length) return "";
    const first = sortedClients[0].marginToDeploy ?? "";
    if (!sortedClients.every((r) => (r.marginToDeploy ?? "") === first)) return "";
    return first;
  }, [sortedClients]);

  const commonMarginPlaceholder = useMemo(() => {
    if (unifiedMarginToDeploy !== "") return "—";
    if (!sortedClients.length) return "—";
    const hasAny = sortedClients.some((r) => String(r.marginToDeploy ?? "").trim() !== "");
    return hasAny ? "Mixed" : "—";
  }, [unifiedMarginToDeploy, sortedClients]);

  const [localCommonMargin, setLocalCommonMargin] = useState("");
  const commonMarginFocusedRef = useRef(false);
  const commonMarginTimerRef = useRef(null);

  useEffect(() => {
    if (!commonMarginFocusedRef.current) {
      setLocalCommonMargin(unifiedMarginToDeploy);
    }
  }, [unifiedMarginToDeploy]);

  const unifiedMultiplier = useMemo(() => {
    if (!sortedClients.length) return "";
    const first = displayMultiplierForRow(sortedClients[0]);
    if (!sortedClients.every((r) => displayMultiplierForRow(r) === first)) return "";
    return first;
  }, [sortedClients]);

  const commonMultiplierPlaceholder = useMemo(() => {
    if (unifiedMultiplier !== "") return "—";
    if (!sortedClients.length) return "—";
    const hasAny = sortedClients.some((r) => String(displayMultiplierForRow(r)).trim() !== "");
    return hasAny ? "Mixed" : "—";
  }, [unifiedMultiplier, sortedClients]);

  const [localCommonMultiplier, setLocalCommonMultiplier] = useState("");
  const commonMultiplierFocusedRef = useRef(false);
  const commonMultiplierTimerRef = useRef(null);

  useEffect(() => {
    if (!commonMultiplierFocusedRef.current) {
      setLocalCommonMultiplier(unifiedMultiplier);
    }
  }, [unifiedMultiplier]);

  useEffect(() => {
    return () => {
      if (commonMarginTimerRef.current) clearTimeout(commonMarginTimerRef.current);
      if (commonMultiplierTimerRef.current) clearTimeout(commonMultiplierTimerRef.current);
    };
  }, []);

  const flushCommonMargin = useCallback(
    (value) => {
      applyMarginToDeployToAllClients?.(value, sortedClientIdsRef.current);
    },
    [applyMarginToDeployToAllClients]
  );

  const handleCommonMarginFocus = () => {
    commonMarginFocusedRef.current = true;
  };

  const handleCommonMarginChange = (e) => {
    const v = e.target.value;
    const clamped = clampMarginInputValue(v);
    setLocalCommonMargin(clamped);
    if (commonMarginTimerRef.current) clearTimeout(commonMarginTimerRef.current);
    commonMarginTimerRef.current = setTimeout(() => flushCommonMargin(clamped), DEBOUNCE_MS);
  };

  const handleCommonMarginBlur = () => {
    if (commonMarginTimerRef.current) {
      clearTimeout(commonMarginTimerRef.current);
      commonMarginTimerRef.current = null;
    }
    const num = parseFloat(localCommonMargin);
    let clamped = localCommonMargin;
    if (localCommonMargin !== "" && !Number.isNaN(num)) {
      if (num > 100) clamped = "100";
      else if (num < 0) clamped = "0";
      setLocalCommonMargin(clamped);
    }
    flushCommonMargin(clamped);
    commonMarginFocusedRef.current = false;
  };

  const flushCommonMultiplier = useCallback(
    (value) => {
      applyMultiplierToAllClients?.(value, sortedClientIdsRef.current);
    },
    [applyMultiplierToAllClients]
  );

  const handleCommonMultiplierFocus = () => {
    commonMultiplierFocusedRef.current = true;
  };

  const handleCommonMultiplierChange = (e) => {
    const v = e.target.value;
    setLocalCommonMultiplier(v);
    if (commonMultiplierTimerRef.current) clearTimeout(commonMultiplierTimerRef.current);
    commonMultiplierTimerRef.current = setTimeout(() => flushCommonMultiplier(v), DEBOUNCE_MS);
  };

  const handleCommonMultiplierBlur = () => {
    if (commonMultiplierTimerRef.current) {
      clearTimeout(commonMultiplierTimerRef.current);
      commonMultiplierTimerRef.current = null;
    }
    flushCommonMultiplier(localCommonMultiplier);
    commonMultiplierFocusedRef.current = false;
  };

  const toggleSortClientCode = useCallback(() => {
    setClientSort((prev) => {
      if (prev.key !== "clientCode") return { key: "clientCode", dir: "asc" };
      return { key: "clientCode", dir: prev.dir === "asc" ? "desc" : "asc" };
    });
  }, []);

  const toggleSortAvailableMargin = useCallback(() => {
    setClientSort((prev) => {
      if (prev.key !== "availableMargin") return { key: "availableMargin", dir: "asc" };
      return { key: "availableMargin", dir: prev.dir === "asc" ? "desc" : "asc" };
    });
  }, []);

  const filteredBaskets = useMemo(() => {
    if (!basketSearch.trim()) return displayBaskets;
    const search = basketSearch.toLowerCase();
    return displayBaskets.filter((basket) => basket.basket_name?.toLowerCase().includes(search));
  }, [displayBaskets, basketSearch]);

  const addToSearchHistory = useCallback((term) => {
    const t = term?.trim();
    if (!t) return;
    setSearchHistory((prev) => (prev.includes(t) ? prev : [t, ...prev].slice(0, MAX_SEARCH_HISTORY)));
  }, []);

  const handleBasketSearchChange = (e) => {
    setBasketSearch(e.target.value);
  };

  const handleBasketSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      if (filteredBaskets.length === 1) {
        addToSearchHistory(filteredBaskets[0].basket_name);
      } else if (basketSearch.trim()) {
        addToSearchHistory(basketSearch);
      }
    }
  };

  const basketTriggerLabel = useMemo(() => {
    if (selectedBasketIndices.size === 0) return "Select basket";
    if (selectedBasketIndices.size === 1) {
      const id = [...selectedBasketIndices][0];
      const b = displayBaskets.find((x) => x.id === id);
      return b?.basket_name ?? "1 basket";
    }
    return `${selectedBasketIndices.size} baskets`;
  }, [selectedBasketIndices, displayBaskets]);

  const renderClientContent = () => {
    if (basketClientsLoading && clientRows.length === 0) {
      return <div className={styles.clientSelectBasketMessage}>Loading clients…</div>;
    }
    if (clientSelectionPhase === "selectedReview" && rowsForTable.length === 0) {
      return (
        <div className={styles.clientSelectBasketMessage}>
          No clients selected. Use Back to update your selection.
        </div>
      );
    }
    if (sortedClients.length === 0) {
      return (
        <div className={styles.clientSelectBasketMessage}>
          {clientSelectionPhase === "selectedReview"
            ? "No matching clients"
            : !hasBasketSelected
              ? "No clients"
              : clientRows.length === 0
                ? "No clients in selected basket(s)"
                : "No matching clients"}
        </div>
      );
    }
    return (
      <table className={styles.clientTable}>
        <thead>
          <tr>
            {isBrowsePhase ? (
              <th className={styles.clientThCheck}>
                <input
                  type="checkbox"
                  className={styles.clientCheckbox}
                  checked={allClientsChecked}
                  ref={headerCheckboxRef}
                  onChange={(e) => onClientHeaderCheckboxChange?.(e.target.checked)}
                  aria-label="Select all clients"
                />
              </th>
            ) : null}
            <th
              className={styles.clientTh}
              aria-sort={
                clientSort.key === "clientCode"
                  ? clientSort.dir === "asc"
                    ? "ascending"
                    : "descending"
                  : "none"
              }
            >
              <button
                type="button"
                className={styles.clientThSortBtn}
                onClick={toggleSortClientCode}
                aria-label={`Sort by Client Code, ${clientSort.key === "clientCode" ? (clientSort.dir === "asc" ? "ascending" : "descending") : "not sorted"}`}
              >
                <span>Client Code</span>
                <GlobalIcon
                  iconName="TableSortIcon"
                  className={`${styles.clientSortIcon} ${clientSort.key === "clientCode" ? styles.clientSortIconActive : ""} ${clientSort.key === "clientCode" && clientSort.dir === "desc" ? styles.clientSortIconDesc : ""}`}
                  aria-hidden
                />
              </button>
            </th>
            <th
              className={styles.clientTh}
              aria-sort={
                clientSort.key === "availableMargin"
                  ? clientSort.dir === "asc"
                    ? "ascending"
                    : "descending"
                  : "none"
              }
            >
              <button
                type="button"
                className={styles.clientThSortBtn}
                onClick={toggleSortAvailableMargin}
                aria-label={`Sort by Available Margin, ${clientSort.key === "availableMargin" ? (clientSort.dir === "asc" ? "ascending" : "descending") : "not sorted"}`}
              >
                <span>Available Margin</span>
                <GlobalIcon
                  iconName="TableSortIcon"
                  className={`${styles.clientSortIcon} ${clientSort.key === "availableMargin" ? styles.clientSortIconActive : ""} ${clientSort.key === "availableMargin" && clientSort.dir === "desc" ? styles.clientSortIconDesc : ""}`}
                  aria-hidden
                />
              </button>
            </th>
            <th className={`${styles.clientTh} ${styles.clientThRight}`}>Margin to Deploy</th>
            <th className={`${styles.clientTh} ${styles.clientThRight}`}>Multiplier</th>
            <th className={`${styles.clientTh} ${styles.clientThRight}`}>Required Margin</th>
          </tr>
          <tr className={styles.clientApplyAllRow}>
            {isBrowsePhase ? (
              <th colSpan={3} className={`${styles.clientTh} ${styles.clientApplyAllLead}`}>
                <span className={styles.clientApplyAllLabel}>Apply to all</span>
              </th>
            ) : (
              <th colSpan={2} className={`${styles.clientTh} ${styles.clientApplyAllLead}`}>
                <span className={styles.clientApplyAllLabel}>Apply to all</span>
              </th>
            )}
            <th className={`${styles.clientTh} ${styles.clientThRight}`}>
              <input
                type="text"
                value={localCommonMargin}
                onFocus={handleCommonMarginFocus}
                onChange={handleCommonMarginChange}
                onBlur={handleCommonMarginBlur}
                className={styles.clientInput}
                placeholder={commonMarginPlaceholder}
                aria-label="Margin to deploy for all clients in this list"
              />
            </th>
            <th className={`${styles.clientTh} ${styles.clientThRight}`}>
              <input
                type="text"
                value={localCommonMultiplier}
                onFocus={handleCommonMultiplierFocus}
                onChange={handleCommonMultiplierChange}
                onBlur={handleCommonMultiplierBlur}
                className={styles.clientInput}
                placeholder={commonMultiplierPlaceholder}
                aria-label="Multiplier for all clients in this list"
              />
            </th>
            <th className={styles.clientTh} aria-hidden />
          </tr>
        </thead>
        <tbody>
          {sortedClients.map((row) => (
            <ClientRow
              key={row.id}
              row={row}
              updateClientRow={updateClientRow}
              showCheckbox={isBrowsePhase}
            />
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div className={styles.combinedSectionWrapper}>
      <div className={styles.combinedSingleColumn}>
        <div className={styles.selectionHeader}>
          <div className={styles.selectionHeaderTitleBlock}>
            <div className={styles.selectionHeaderLeft}>
              <ClientIcon />
              <span className={styles.selectionHeaderTitle}>CLIENT SELECTION</span>
            </div>
            <HeaderHelpIcon />
          </div>
          <div className={styles.selectionHeaderInputs}>
            <div className={styles.searchInputWrapper}>
              <input
                type="text"
                placeholder="Search Client Code"
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
                className={styles.searchInput}
              />
            </div>
            {isBrowsePhase ? (
              <div
                className={`${styles.basketSelectWrap}${isBasketDropdownOpen ? ` ${styles.basketSelectWrapOpen}` : ""}`}
                ref={basketSelectWrapRef}
              >
                <button
                  type="button"
                  className={styles.basketSelectTrigger}
                  aria-expanded={isBasketDropdownOpen}
                  aria-haspopup="listbox"
                  onClick={() => setIsBasketDropdownOpen((o) => !o)}
                >
                  <span className={styles.basketSelectTriggerLabel}>{basketTriggerLabel}</span>
                  <GlobalIcon iconName="ChevronDownIcon" width={14} height={14} aria-hidden />
                </button>
                {isBasketDropdownOpen && (
                  <div className={styles.basketDropdown} role="listbox" aria-label="Baskets">
                    <div className={styles.basketDropdownSearchRow}>
                      <GlobalIcon iconName="SearchIcon" width={14} height={14} className={styles.basketDropdownSearchIcon} aria-hidden />
                      <input
                        ref={basketDropdownSearchRef}
                        type="text"
                        placeholder="Search"
                        value={basketSearch}
                        onChange={handleBasketSearchChange}
                        onKeyDown={handleBasketSearchKeyDown}
                        className={styles.basketDropdownSearchInput}
                      />
                    </div>
                    <div className={styles.basketDropdownScroll}>
                      <div className={styles.basketDropdownItems}>
                        {displayBaskets.length === 0 ? (
                          <span className={styles.basketEmptyText}>No baskets available</span>
                        ) : filteredBaskets.length === 0 ? (
                          <span className={styles.basketEmptyText}>No results found</span>
                        ) : (
                          filteredBaskets.map((basket) => {
                            const selected = selectedBasketIndices.has(basket.id);
                            return (
                              <label
                                key={basket.id}
                                className={`${styles.basketDropdownItem} ${selected ? styles.basketDropdownItemSelected : styles.basketDropdownItemUnselected}`}
                              >
                                <input
                                  type="checkbox"
                                  className={styles.basketDropdownCheckbox}
                                  checked={selected}
                                  onChange={() => toggleBasket(basket.id)}
                                />
                                <span className={styles.basketDropdownItemText}>{basket.basket_name}</span>
                              </label>
                            );
                          })
                        )}
                      </div>
                    </div>
                    {searchHistory.length > 0 && (
                      <div className={styles.basketDropdownHistory}>
                        <div className={styles.basketDropdownLabel}>Search history</div>
                        <div className={styles.basketHistoryChips}>
                          {searchHistory.map((term, idx) => (
                            <button
                              key={`${term}-${idx}`}
                              type="button"
                              className={styles.basketHistoryChip}
                              onClick={() => setBasketSearch(term)}
                            >
                              {term}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : null}
          </div>
          <div className={styles.selectionHeaderActions}>
            {isBrowsePhase ? (
              <button
                type="button"
                className={styles.clientSelectionNextBtn}
                onClick={onClientSelectionNext}
                disabled={!someClientsChecked}
              >
                Next
              </button>
            ) : (
              <button type="button" className={styles.clientSelectionBackBtn} onClick={onClientSelectionBack}>
                Back
              </button>
            )}
          </div>
        </div>
        <div className={styles.clientTableWrapper}>{renderClientContent()}</div>
      </div>
    </div>
  );
}

export default React.memo(CombinedSelectionSection);
